import { Suspense } from 'react';
import {
  useProduct,
  useSelectedVariant
} from '@/components/product/product-context';
import { SKELETON_PROPS } from '@/lib/constants/skeleton';
import ColumnSelectorHeader from './ColumnSelectorHeader';
import HelpModalLink from './HelpModalLink';
import OptionCard from './OptionCard';
import {
  resolveColumnOption,
  selectedOptionValue
} from './resolve-column-option';
import { safePriceDifference } from './calculate-price-difference';
import { useColumnOptionClick } from './use-column-option-click';
import { useVariantHelpModal } from './use-variant-help-modal';

/**
 * A selector that lists out all the option values as radio buttons.
 * It lets you have a modal and shows a price difference between the base level
 * option and any upgrades.
 */
export default function RadioSelector({ settings }) {
  const { productData } = useProduct();
  const helpModal = useVariantHelpModal(settings);

  // Like CheckBoxSelector, this component has a lot of required settings
  // that are not super obvious so let's validate first.
  const result = resolveColumnOption(settings, productData);
  if (!result.ok) {
    console.warn(`[RadioSelector] ${result.error}`);
    return null;
  }

  const { option, columnSettings, baseOptionValue } = result.data;

  if (option.values.length <= 1) {
    console.warn(
      '[RadioSelector] Only one option value, so no selector needed'
    );
    return null;
  }

  const cards = option.values.map((value, index) => {
    const optionCopy = columnSettings?.option_copy?.find(
      (o) => o?.option_value === value
    );
    return {
      value,
      inputId: `detailed-variant-radio-${index}`,
      title: optionCopy?.title ?? value,
      description: optionCopy?.description
    };
  });

  return (
    <>
      <ColumnSelectorHeader
        label={settings?.label ?? option.name}
        helpModal={helpModal}
      />
      <Suspense
        fallback={
          <RadioCards
            cards={cards}
            optionName={option.name}
            {...SKELETON_PROPS}
          />
        }
      >
        <SelectedRadioCards
          cards={cards}
          option={option}
          baseOptionValue={baseOptionValue}
        />
      </Suspense>
      <HelpModalLink
        helpModal={helpModal}
        position="Bottom Left"
        className="mt-3"
      />
    </>
  );
}

/**
 * Reads the selected variant and selection actions, so it needs to be wrapped
 * in a Suspense tag. Renders the same list as the fallback, with the selection,
 * the prices and the click handler filled in.
 */
const SelectedRadioCards = ({ cards, option, baseOptionValue }) => {
  const { productData } = useProduct();
  const { variant } = useSelectedVariant();
  const selectOption = useColumnOptionClick();

  const priceDifferences = Object.fromEntries(
    cards.map(({ value }) => [
      value,
      safePriceDifference(
        'RadioSelector',
        productData?.shopifyProduct,
        variant,
        baseOptionValue,
        value,
        option
      )
    ])
  );

  return (
    <RadioCards
      cards={cards}
      optionName={option.name}
      currentValue={selectedOptionValue(variant, option)}
      priceDifferences={priceDifferences}
      onSelect={selectOption}
      data-cy="column-radio-selector"
    />
  );
};

/**
 * The card list itself that is presentational and does not suspend.
 */
const RadioCards = ({
  cards,
  optionName,
  currentValue,
  priceDifferences,
  onSelect,
  ...listProps
}) => (
  <div className="mt-2 flex flex-col gap-3" {...listProps}>
    {cards.map(({ value, inputId, title, description }) => {
      const isSelected = currentValue === value;

      return (
        <OptionCard
          key={value}
          selected={isSelected}
          onClick={onSelect && (() => onSelect(value))}
          rowClassName="items-center"
          title={title}
          titleFor={inputId}
          description={description}
          priceDifference={priceDifferences?.[value]}
          control={
            <>
              <input
                id={inputId}
                type="radio"
                name={optionName}
                value={value}
                checked={isSelected}
                readOnly
                className="sr-only"
                tabIndex="-1"
              />
              <div
                className={`relative flex h-4 w-4 items-center justify-center rounded-full border ${isSelected ? 'border-light-green' : 'bg-parlor-green-80 border-white'}`}
              >
                <span
                  className={`bg-light-green block h-2 w-2 rounded-full ${isSelected ? 'visible' : 'invisible'}`}
                />
              </div>
            </>
          }
        />
      );
    })}
  </div>
);
